
import React, { useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import Header from './components/Header';
import { MobileNavBar } from './components/MobileNavBar';
import { MobileMenu } from './components/MobileMenu';
import Loading from './components/Loading';
import SplashScreen from './components/SplashScreen';

// Lazy Load Heavy Components
const SwisstopoMap = React.lazy(() => import('./components/SwisstopoMap'));
const AdvisorChat = React.lazy(() => import('./components/AdvisorChat'));
const RoutePlanner = React.lazy(() => import('./components/RoutePlanner'));
const ProfileManager = React.lazy(() => import('./components/ProfileManager'));
const TrackLibrary = React.lazy(() => import('./components/TrackLibrary'));
const FlightBuddies = React.lazy(() => import('./components/FlightBuddies'));
const Messages = React.lazy(() => import('./components/Messages'));
import LegalModal from './components/LegalModal';
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard'));
const InvoiceView = React.lazy(() => import('./components/InvoiceView'));
const Marketplace = React.lazy(() => import('./components/Marketplace'));
const FlightCompare = React.lazy(() => import('./components/FlightCompare'));
const SubscriptionModal = React.lazy(() => import('./components/SubscriptionModal'));
const PlatformAnalytics = React.lazy(() => import('./components/PlatformAnalytics'));
const SurveyLandingPage = React.lazy(() => import('./components/SurveyLandingPage'));
const VerificationPage = React.lazy(() => import('./components/VerificationPage'));
const PricingView = React.lazy(() => import('./components/PricingView'));
import { FeatureListModal } from './components/FeatureListModal';


import Auth from './components/Auth';
import UserManagement from './components/UserManagement';
import RouteExport from './components/RouteExport';
import { FlightProfile, RouteWaypoint, RouteAnalysis, User, FlownTrack, MapPlan } from './types';
import { translations, Language } from './translations';
import { FlightAnalysisData } from './components/FlightAnalysisPanel';
import { evaluateRoute } from './utils/fai-calculator';

import { FeedbackDialog } from './components/FeedbackDialog';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './components/LandingPage';
import ProFeatureBadge from './components/ProFeatureBadge';

import PaymentStatusModal from './components/PaymentStatusModal';
import NewsOverlay from './components/NewsOverlay';
import { ApiService, API_BASE_URL } from './services/api';
import { SubscriptionPermissions } from './types';
import { Gem, Lock, Camera, Scissors, Sparkles, Loader2, Check, X, Monitor, Newspaper, Share2, Image as ImageIcon, BarChart2 } from 'lucide-react';

// Helper: Set user permissions based on subscription plan permissions from DB
const applyPermissions = (u: User): User => {
  // If user has plan permissions from the backend, use those
  const hasActiveSub = u.subscription?.status === 'active' || u.subscription?.status === 'trial';
  
  if (u.permissions && (u.subscription_plan_id || hasActiveSub)) {
    return u;
  }

  // Fallback for Free users (no plan) — restrictive defaults
  const FREE_PERMISSIONS: SubscriptionPermissions = {
    upload_tracks: true,
    ai_track_creation: false,
    ai_track_analysis: false,
    messaging: true,
    profile_management: true,
    flight_buddies: true,
    track_planning: true,           // Manuell für alle!
    planned_track_analysis: false,
    buy_tokens: false,
    risk_check: false,
    ai_consultation: false,
    manage_elements: true,
    statistics: true,
    glide_range: false,
  };
  u.permissions = FREE_PERMISSIONS;
  return u;
};

const App: React.FC = () => {
  // ... existing state ...

  // Payment Status State
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'failed' | 'cancel' | 'verifying' | 'pending' | null>(null);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState<string | null>(null);

  useEffect(() => {
    // Check for Payrexx callback in URL
    const checkPayment = async () => {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);

      const isSuccess = path.includes('/payment/success') || path.includes('/app/payment-success') || params.get('payment') === 'success';
      const isFailed = path.includes('/payment/failed') || path.includes('/app/payment-failed') || params.get('payment') === 'failed';
      const isCancel = path.includes('/payment/cancel') || path.includes('/app/payment-cancel') || params.get('payment') === 'cancelled';

      if (isSuccess) {
        const txId = params.get('txId') || params.get('invoice') || params.get('referenceId');
        if (txId) {
          setPaymentStatus('verifying');
          try {
            const res = await ApiService.verifyPayment(txId);
            if (res.status === 'confirmed') {
              setPaymentStatus('success');

              // Refresh user data (Tokens/Subscription)
              try {
                const updatedUser = await ApiService.getProfile();
                applyPermissions(updatedUser);
                setUser(updatedUser);
                localStorage.setItem('xc_session_user', JSON.stringify(updatedUser)); // Persist
                console.log("User profile refreshed after payment", updatedUser);
              } catch (err) {
                console.error("Failed to refresh user profile after payment", err);
              }
            } else {
              setPaymentStatus('failed'); // or 'pending' if not confirmed
            }
          } catch (e) {
            console.error("Payment Verification Failed", e);
            setPaymentStatus('failed');
          }
          setPaymentInvoiceId(txId);
        } else {
          setPaymentStatus('success'); // Legacy fallback
        }
        window.history.replaceState({}, document.title, "/");
      } else if (isFailed) {
        setPaymentStatus('failed');
        setPaymentInvoiceId(params.get('txId') || params.get('invoice'));
        window.history.replaceState({}, document.title, "/");
      } else if (isCancel) {
        setPaymentStatus('cancel');
        window.history.replaceState({}, document.title, "/");
      }
    };

    checkPayment();
  }, []);


  // ... rest of App ...



  // Sync Initialization to prevent "Flash of Login"
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('xc_session_user');
    return saved ? JSON.parse(saved) : null;
  });

  const [view, setView] = useState<'map' | 'chat' | 'planner' | 'profile' | 'admin' | 'library' | 'buddies' | 'messages' | 'market' | 'menu' | 'compare' | 'statistics'>('map');
  const [previousView, setPreviousView] = useState<'map' | 'chat' | 'planner' | 'profile' | 'admin' | 'library' | 'buddies' | 'messages' | 'market' | 'menu' | 'compare' | 'statistics' | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<any>(null);
  const [showProfileLockModal, setShowProfileLockModal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalTab, setLegalTab] = useState<'impressum' | 'privacy' | 'terms' | 'datasources'>('privacy');
  const [lang, setLang] = useState<Language>('de');
  const [route, setRoute] = useState<RouteWaypoint[]>([]);
  const [flownTrack, setFlownTrack] = useState<FlownTrack | null>(null); // New state for IGC
  const [flightTrackId, setFlightTrackId] = useState<string | null>(null); // Track ID for saving chat history
  const [initialChatHistory, setInitialChatHistory] = useState<any[] | null>(null); // Restored chat history
  const [comparisonTracks, setComparisonTracks] = useState<{ track: [number, number][], date: string, color?: string }[]>([]); // Multi-track state
  const [analysis, setAnalysis] = useState<RouteAnalysis | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [hasKey, setHasKey] = useState<boolean>(true);
  const [draftRecipient, setDraftRecipient] = useState<User | null>(null); // Draft Message for Buddies
  // Global Direct Snipping States
  const [globalSnipActive, setGlobalSnipActive] = useState(false);
  const [globalCropStart, setGlobalCropStart] = useState<{ x: number, y: number } | null>(null);
  const [globalCropEnd, setGlobalCropEnd] = useState<{ x: number, y: number } | null>(null);
  
  const [showFeatureList, setShowFeatureList] = useState(false);
  const [showGlobalNewsModal, setShowGlobalNewsModal] = useState(false);
  const [globalSnipTarget, setGlobalSnipTarget] = useState<'news' | 'social'>('news');
  const [socialBgTitle, setSocialBgTitle] = useState<string>('');
  const [socialBgCategory, setSocialBgCategory] = useState<string>('Screenshots');
  const [isGlobalSocialSaving, setIsGlobalSocialSaving] = useState<boolean>(false);
  const [globalNewsItem, setGlobalNewsItem] = useState<{
    title: string;
    content: string;
    icon: string;
    category: string;
    active: boolean;
    image_url?: string;
  } | null>(null);
  const [isGlobalNewsSaving, setIsGlobalNewsSaving] = useState(false);
  const [isGlobalAiAnalyzing, setIsGlobalAiAnalyzing] = useState(false);

  useEffect(() => {
    const handleOpenFeatures = () => setShowFeatureList(true);
    window.addEventListener('open-feature-list', handleOpenFeatures);
    return () => window.removeEventListener('open-feature-list', handleOpenFeatures);
  }, []);

  useEffect(() => {
    const handleGlobalSnipStart = () => {
      const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
      if (!isAdmin) return;

      // Direktes Overlay über der originalen App-Seite aktivieren
      setGlobalSnipActive(true);
      setGlobalCropStart(null);
      setGlobalCropEnd(null);
    };

    window.addEventListener('global-snip-start', handleGlobalSnipStart);
    return () => window.removeEventListener('global-snip-start', handleGlobalSnipStart);
  }, [user]);

  const handleGlobalOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setGlobalCropStart({ x: e.clientX, y: e.clientY });
    setGlobalCropEnd({ x: e.clientX, y: e.clientY });
  };

  const handleGlobalOverlayMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!globalCropStart) return;
    setGlobalCropEnd({ x: e.clientX, y: e.clientY });
  };

  const handleGlobalOverlayMouseUp = async () => {
    if (!globalCropStart || !globalCropEnd) return;
    
    const x = Math.min(globalCropStart.x, globalCropEnd.x);
    const y = Math.min(globalCropStart.y, globalCropEnd.y);
    const w = Math.abs(globalCropStart.x - globalCropEnd.x);
    const h = Math.abs(globalCropStart.y - globalCropEnd.y);

    // Wenn der Bereich winzig ist (z.B. nur ein normaler Klick), machen wir nichts und schließen das Overlay
    if (w < 10 || h < 10) {
        setGlobalSnipActive(false);
        setGlobalCropStart(null);
        setGlobalCropEnd(null);
        return;
    }

    // Erst das Overlay ausblenden, damit es nicht fotografiert wird
    setGlobalSnipActive(false);
    setGlobalCropStart(null);
    setGlobalCropEnd(null);
    
    document.body.style.cursor = 'wait';
    
    // Kurze Pause zum Ausblenden des Overlays, dann html2canvas-Aufnahme der echten DOM-Struktur
    setTimeout(async () => {
      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(document.body, {
          useCORS: true,
          allowTaint: true,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
          x: x + window.scrollX,
          y: y + window.scrollY,
          width: w,
          height: h,
          windowWidth: document.documentElement.clientWidth,
          windowHeight: document.documentElement.clientHeight
        });
        
        const dataUrl = canvas.toDataURL('image/png');
        
        // Modal mit dem perfekten Ausschnitt öffnen
        setGlobalNewsItem({
          title: '',
          content: '',
          icon: '<Newspaper className="w-4 h-4 inline mr-1" />',
          category: 'Allgemein',
          active: true,
          image_url: dataUrl
        });
        setShowGlobalNewsModal(true);
      } catch (err) {
        console.error("Global direct capture failed:", err);
        alert("Screenshot-Aufnahme fehlgeschlagen.");
      } finally {
        document.body.style.cursor = 'default';
      }
    }, 80);
  };

  const drawOverlayCanvas = (canvas: HTMLCanvasElement) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w;
    canvas.height = h;

    // Vollflächig abdunkeln
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, 0, w, h);

    if (globalCropStart && globalCropEnd) {
      const cx = Math.min(globalCropStart.x, globalCropEnd.x);
      const cy = Math.min(globalCropStart.y, globalCropEnd.y);
      const cw = Math.abs(globalCropStart.x - globalCropEnd.x);
      const ch = Math.abs(globalCropStart.y - globalCropEnd.y);

      // Den gezogenen Bereich transparent freistellen
      ctx.clearRect(cx, cy, cw, ch);

      // Grüner Auswahlrahmen
      ctx.strokeStyle = '#10b981'; // Emerald 500
      ctx.lineWidth = 2;
      ctx.strokeRect(cx, cy, cw, ch);
    }
  };

  const handleGlobalNewsAiAnalyze = async () => {
    if (!globalNewsItem?.image_url) return;
    setIsGlobalAiAnalyzing(true);
    try {
      const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
      const res = await fetch('/api/management/news/analyze-image', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ image: globalNewsItem.image_url })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.title && data.content) {
          setGlobalNewsItem(prev => prev ? { ...prev, title: data.title, content: data.content } : null);
        }
      } else {
        alert("KI-Analyse fehlgeschlagen.");
      }
    } catch (err) {
      console.error("AI analysis failed:", err);
      alert("KI-Analyse fehlgeschlagen.");
    } finally {
      setIsGlobalAiAnalyzing(false);
    }
  };

  const handleGlobalNewsSave = async () => {
    if (!globalNewsItem) return;
    if (!globalNewsItem.title.trim()) {
        alert("Bitte einen Titel eingeben.");
        return;
    }
    
    setIsGlobalNewsSaving(true);
    let finalImageUrl = globalNewsItem.image_url || '';

    if (finalImageUrl.startsWith('data:')) {
        try {
            const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
            const uploadRes = await fetch('/api/management/files', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: 'news-screenshot.png',
                    mime_type: 'image/png',
                    data: finalImageUrl
                })
            });
            if (uploadRes.ok) {
                const uploadData = await uploadRes.json();
                finalImageUrl = uploadData.blob_url;
            } else {
                alert("Fehler beim Hochladen des Screenshots.");
                setIsGlobalNewsSaving(false);
                return;
            }
        } catch (err) {
            console.error("Upload error:", err);
            alert("Fehler beim Hochladen des Screenshots.");
            setIsGlobalNewsSaving(false);
            return;
        }
    }

    try {
        const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
        const res = await fetch('/api/management/news', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ ...globalNewsItem, image_url: finalImageUrl })
        });
        if (res.ok) {
            setShowGlobalNewsModal(false);
            setGlobalNewsItem(null);
            window.dispatchEvent(new CustomEvent('news-created-reload'));
            alert("News-Meldung erfolgreich gespeichert und veröffentlicht!");
        } else {
            const err = await res.json().catch(() => ({}));
            alert(`Fehler beim Speichern: ${err.error || 'Unbekannter Fehler'}`);
        }
    } catch (e) {
        console.error(e);
        alert('Fehler beim Verbinden mit dem Server.');
    } finally {
        setIsGlobalNewsSaving(false);
    }
  };

  const handleGlobalSocialSave = async () => {
    if (!globalNewsItem?.image_url) return;
    const title = socialBgTitle.trim() || globalNewsItem.title.trim() || `Screenshot ${new Date().toLocaleDateString('de-CH')}`;
    setIsGlobalSocialSaving(true);
    try {
        const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
        const res = await fetch('/api/management/social/backgrounds/upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                dataUrl: globalNewsItem.image_url,
                title: title,
                category: socialBgCategory || 'Screenshots'
            })
        });
        if (res.ok) {
            setShowGlobalNewsModal(false);
            setGlobalNewsItem(null);
            setSocialBgTitle('');
            window.dispatchEvent(new CustomEvent('social-backgrounds-reload'));
            alert("Bild erfolgreich in der Social Media Mediathek gespeichert!");
        } else {
            const err = await res.json().catch(() => ({}));
            alert(`Fehler beim Speichern in Mediathek: ${err.error || 'Unbekannter Fehler'}`);
        }
    } catch (e: any) {
        console.error(e);
        alert(`Fehler beim Speichern: ${e.message || 'Verbindung fehlgeschlagen'}`);
    } finally {
        setIsGlobalSocialSaving(false);
    }
  };
  const [chatPrompt, setChatPrompt] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const [mapTarget, setMapTarget] = useState<{ lat: number, lng: number, zoom: number } | null>(null);
  const [chatMapAction, setChatMapAction] = useState<{lat: number, lng: number, label: string} | null>(null);
  const [chatMapActions, setChatMapActions] = useState<{lat: number, lng: number, label: string}[] | null>(null);
  const [analysisTarget, setAnalysisTarget] = useState<any>(null); // Bridge for Analysis
  const [mapPlan, setMapPlan] = useState<MapPlan | null>(null); // v2.0 Plan State
  const [testModeName, setTestModeName] = useState<string | null>(null);
  const [flightAnalysisData, setFlightAnalysisData] = useState<FlightAnalysisData | null>(null);

  // Badge States
  const [unreadMsgCount, setUnreadMsgCount] = useState(0);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const [unreadSharedCount, setUnreadSharedCount] = useState(0);

  const [mapWidth, setMapWidth] = useState(() => {
    const saved = localStorage.getItem('xc_map_width');
    return saved ? parseFloat(saved) : 80; // Default 80% Map (20% Planner) - User requested shorter default
  });
  const mapWidthRef = useRef(mapWidth);
  const panelWidthSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  // Manual Route Planning State (Lifted)
  const [isManualPlanning, setIsManualPlanning] = useState(false);
  const [loadedMissionId, setLoadedMissionId] = useState<string | null>(null);
  const [loadedMissionName, setLoadedMissionName] = useState<string | null>(null);

  // Shared Waypoint AGL State (Sync between Map Popup and Sidebar)
  const [waypointAgl, setWaypointAgl] = useState<Record<number, number>>({});
  
  // Explicitly controlled subscription modal
  const [showPricingModal, setShowPricingModal] = useState<{ show: boolean, tab: 'plans' | 'tokens' }>({ show: false, tab: 'plans' });
  
  useEffect(() => {
    const handleOpen = (e: any) => setShowPricingModal({ show: true, tab: e.detail?.tab || 'plans' });
    window.addEventListener('open-pricing', handleOpen);
    
    const handleNavToMap = () => {
      if (window.innerWidth < 1024) {
        setView('map');
      }
    };
    window.addEventListener('nav-to-map', handleNavToMap);
    
    const handleAuthExpired = () => {
      alert("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");
      handleLogout();
    };
    window.addEventListener('auth-expired', handleAuthExpired);
    
    return () => {
      window.removeEventListener('open-pricing', handleOpen);
      window.removeEventListener('nav-to-map', handleNavToMap);
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, [user]);
  const [plannerConfig, setPlannerConfig] = useState({
     activeTool: 'pan' as 'pan' | 'start' | 'turnpoint' | 'end',
     routeVariants: [] as any[][],
     activeVariantIndex: 0,
     glideRangeEnabled: false,
     glideRatioMargin: 0,
     showHeightLabels: true
  });

  const [glideRangeParams, setGlideRangeParams] = useState<{ lat: number; lng: number; altitudeM: number; glideRatio: number } | null>(null);
  const [plannerCustomWind, setPlannerCustomWind] = useState<{dir: number, speed: number} | null>(null);

  // Auto-sync is handled exclusively by RoutePlanner.tsx now to prevent race conditions.

  // Ref for updating CSS variable directly (performance & lint fix)
  const mainRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    if (mainRef.current) {
      mainRef.current.style.setProperty('--split-width', `${mapWidth}%`);
    }
  }, [mapWidth]);

  // Custom Global Alert Override
  const [globalAlert, setGlobalAlert] = useState<string | null>(null);



  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (message: string) => {
      // Use setTimeout to ensure this can be safely called from anywhere (even from outside React state loops)
      setTimeout(() => setGlobalAlert(message), 0);
    };
    return () => {
      window.alert = originalAlert;
    };
  }, []);

  const [showLanding, setShowLanding] = useState(() => {
    // Falls der User bereits eingeloggt ist, direkt in die App starten!
    const savedUser = localStorage.getItem('xc_session_user');
    if (savedUser) return false;
    return true;
  });

  const [authIntent, setAuthIntent] = useState<'login' | 'register' | 'buy'>('login');

  // Initialize Profile (Lazy)
  const [profile, setProfile] = useState<FlightProfile>(() => {
    // START EMPTY by default to trigger "Profile Incomplete" badge
    return {
      activeGliderId: '',
      activeHarnessId: '',
      gliders: [],
      harnesses: [],
      stats: {
        age: 0, fitness: '', mentalState: '', reaction: '', riskTolerance: '',
        disciplines: [], startYear: new Date().getFullYear(), licenseStartYear: new Date().getFullYear(), flightsPerYear: 0, hoursPerYear: 0,
        educationLevel: '', trainingHabits: [],
        talent: '', groundHandling: '', launchTechnique: '', lastSIV: '',
        meteoKnowledge: '', planningStyle: '', abortRate: '', postFlightAnalysis: '',
        socialEnvironment: '', preflightCheck: 'Gewissenhaft',
        terrain: [], conditions: '', season: [], regions: [],
        furthestXC: 0
      },
      // Computed helpers
      glideRatio: 8,
      experienceLevel: 'Student',
      skillLevel: 1,
      maxDistance: 0,
      maxFai: 0,
      preferredRegion: 'Schweiz'
    };
  });

  const isProfileIncomplete = useMemo(() => {
    if (!user) return false;
    const prof = profile;
    if (!prof) return true;
    const stats = prof.stats;
    return !stats ||
      !stats.fitness ||
      (!stats.disciplines || stats.disciplines.length === 0) ||
      !stats.groundHandling ||
      (!prof.gliders || prof.gliders.length === 0) ||
      (!prof.harnesses || prof.harnesses.length === 0);
  }, [user, profile]);

  // Helper Functions defined early to avoid ReferenceError
  const loadUserData = (userId: string, fallbackProfile?: FlightProfile | null) => {
    try {
      const raw = localStorage.getItem('xc_profiles');
      let profiles = raw ? JSON.parse(raw) : {};

      if (profiles && profiles[userId]) {
        console.log("Loaded profile from local cache");
        setProfile(profiles[userId]);
      } else if (fallbackProfile) {
        console.log("Local cache empty. Restoring profile from cloud backup.");
        setProfile(fallbackProfile);
        // Persist to local storage to restore cache
        if (!profiles) profiles = {};
        profiles[userId] = fallbackProfile;
        localStorage.setItem('xc_profiles', JSON.stringify(profiles));
      }
    } catch (e) {
      console.error("Failed to load user profile:", e);
    }
  };

  const handleLogout = () => {
    // Prevent logout for God Mode user to avoid reset loops
    if (user?.id === "forced-admin-id") {
      console.log("God Mode Active - Suppressing Logout");
      return;
    }
    setUser(null);
    localStorage.removeItem('xc_session_user');
    localStorage.removeItem('xc_auth_token');
    localStorage.removeItem('auth_token');
    setRoute([]);
    setAnalysis(null);
    setView('map');
    
    // Force a hard reload and clear service workers to escape cached Error Boundary loops
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        for (let registration of registrations) {
          registration.unregister();
        }
      }).finally(() => {
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  };

  const handleLogin = (u: User) => {
    try {
      // Apply permissions based on subscription tier
      applyPermissions(u);
      setUser(u);
      localStorage.setItem('xc_session_user', JSON.stringify(u));
      loadUserData(u.id, u.pilot_profile);

      // If user came via "Buy" flow and doesn't have an active plan, open pricing immediately
      if (authIntent === 'buy' && !u.subscription_plan_id && u.subscription?.status !== 'active') {
         setShowPricingModal({ show: true, tab: 'plans' });
      }
    } catch (e) {
      console.error("Login Error:", e);
    }
  };

  useEffect(() => {
    if (user) {
      if (!user.id || !user.email) {
        console.warn("Corrupt session detected. Logging out.");
        handleLogout();
        return;
      }

      loadUserData(user.id, user.pilot_profile);

      // Force refresh user profile from API to ensure tokens/subscription are up to date
      ApiService.getProfile()
        .then(freshUser => {
          // Only update if different to avoid loops/renders? 
          // Actually, we want to update if tokens changed.
          // JSON.stringify comparison is safe enough for this size.
          if (JSON.stringify(freshUser) !== JSON.stringify(user)) {
            console.log("Profile refreshed from API", freshUser);
            applyPermissions(freshUser);
            setUser(freshUser);
            localStorage.setItem('xc_session_user', JSON.stringify(freshUser));

            // Sync Pilot Profile if available from backend
            if (freshUser.pilot_profile) {
              console.log("Using Cloud Pilot Profile", freshUser.pilot_profile);
              setProfile(freshUser.pilot_profile);
            }
          }
        })
        .catch(err => console.warn("Background profile refresh failed", err));

      // Load panel width from user settings (server-side)
      ApiService.getMapSettings()
        .then(settings => {
          if (settings && settings.panel_width != null) {
            const w = parseFloat(settings.panel_width);
            if (w > 20 && w < 95) {
              setMapWidth(w);
              mapWidthRef.current = w;
              localStorage.setItem('xc_map_width', w.toString());
            }
          }
        })
        .catch(() => {}); // Silently fail – localStorage fallback is already in place
    }
  }, [user?.id]); // Only run if ID changes or on mount (if user exists), NOT on every user change to avoid loop

  const [hasDismissedPricing, setHasDismissedPricing] = useState(() => localStorage.getItem('xc_pricing_dismissed') === 'true');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Admin Test Mode Listener
  useEffect(() => {
    const handleAdminSwitch = (e: CustomEvent) => {
      if (!user) return;
      const detail = e.detail;
      if (detail) {
        // ... existing admin logic ...
        console.log("Admin Test Mode Event Received:", detail);
        // ...
        setTestModeName(detail.planName || 'Test Mode');
      } else if (detail === null) {
        setTestModeName(null);
        // ... existing reset logic ...
      }
    };

    const handleSwitchView = (e: CustomEvent) => {
      if (e.detail) {
        setView(e.detail);
        if (e.detail === 'planner') {
          setIsManualPlanning(true);
        }
      }
    };


    const handleUserRefresh = (e: CustomEvent) => {
      if (e.detail) {
        setUser(prev => ({ ...(prev || {}), ...e.detail }));
        const current = JSON.parse(localStorage.getItem('xc_session_user') || localStorage.getItem('xc_user') || '{}');
        const merged = { ...current, ...e.detail };
        localStorage.setItem('xc_session_user', JSON.stringify(merged));
        localStorage.setItem('xc_user', JSON.stringify(merged));
      }
    };

    const handleGlobalAlert = (e: CustomEvent) => {
       if (e.detail) {
          setGlobalAlert(e.detail);
       }
    };

    window.addEventListener('admin-switch-plan' as any, handleAdminSwitch as any);
    window.addEventListener('switch-view' as any, handleSwitchView as any);
    window.addEventListener('user-refresh' as any, handleUserRefresh as any);
    window.addEventListener('user-updated' as any, handleUserRefresh as any);
    window.addEventListener('global-alert' as any, handleGlobalAlert as any);

    const handleShowLegal = (e: any) => {
      if (e.detail && typeof e.detail === 'string') {
        setLegalTab(e.detail as any);
      } else {
        setLegalTab('privacy'); // Default fallback
      }
      setShowLegalModal(true);
    };
    window.addEventListener('show-legal-modal', handleShowLegal);
    const handleNavToMap = () => setView('map');
    window.addEventListener('nav-to-map', handleNavToMap);

    return () => {
      window.removeEventListener('admin-switch-plan' as any, handleAdminSwitch as any);
      window.removeEventListener('switch-view' as any, handleSwitchView as any);
      window.removeEventListener('nav-to-map', handleNavToMap);
      window.removeEventListener('user-refresh' as any, handleUserRefresh as any);
      window.removeEventListener('user-updated' as any, handleUserRefresh as any);
      window.removeEventListener('global-alert' as any, handleGlobalAlert as any);
      window.removeEventListener('show-legal-modal', handleShowLegal);
    };
  }, [user]);


  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const percentage = (e.clientX / window.innerWidth) * 100;
      if (percentage > 20 && percentage < 95) {
        setMapWidth(percentage);
        mapWidthRef.current = percentage;
        localStorage.setItem('xc_map_width', percentage.toString());
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = 'default';
      // Save panel width to user profile (debounced)
      if (panelWidthSaveTimerRef.current) clearTimeout(panelWidthSaveTimerRef.current);
      panelWidthSaveTimerRef.current = setTimeout(() => {
        ApiService.saveMapSettings({ panel_width: mapWidthRef.current }).catch(e => console.warn('Panel width save failed:', e));
      }, 500);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Poll for Friend Requests (Badge) & Unread Messages
  useEffect(() => {
    if (!user) return;
    const checkRequests = async () => {
      try {
        const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
        if (token) {
          // 1. Friend Requests
          const res = await ApiService.apiRequest('/friends/requests');
          if (res.ok) {
            const data = await res.json();
            setFriendRequestCount(data.length);
          }

          // 2. Unread Messages
          const resMsg = await ApiService.apiRequest('/messages?unread_count=true');
          if (resMsg.ok) {
            const data = await resMsg.json();
            setUnreadMsgCount(data.count || 0);
          }

          // 3. Unread Shared Routes
          const resShared = await ApiService.getUserMissions(user.id);
          const unread = Array.isArray(resShared) ? resShared.filter((m: any) => m.isShared && !m.isOutgoingShare && !m.isRead).length : 0;
          setUnreadSharedCount(unread);
        }
      } catch (e) { }
    };
    const interval = setInterval(checkRequests, 10000); // Check every 10s
    checkRequests(); // Initial
    return () => clearInterval(interval);
  }, [user]);

  const handleApiError = (error: any) => {
    console.error("Global API Error Handler:", error);
    const msg = error?.message || "";
    // Only invalidate if explicitly "API key not valid"
    // 400/401/403 are often standard HTTP errors (e.g. invalid auth token, bad request)
    // and should NOT trigger the API key invalid warning masking the entire app
    if (msg.includes("API key not valid") || msg.includes("Gemini API key")) {
      setHasKey(false);
    }
  };

  const saveProfile = (newProfile: FlightProfile) => {
    if (!user) return;
    try {
      setProfile(newProfile);

      // Local Backup
      const raw = localStorage.getItem('xc_profiles');
      const profiles = raw ? JSON.parse(raw) : {};
      profiles[user.id] = newProfile;
      localStorage.setItem('xc_profiles', JSON.stringify(profiles));

      // Cloud Persist
      ApiService.savePilotProfile(newProfile)
        .then(res => {
          console.log("Profile saved to cloud", res);
          // Update timestamp in user object to track staleness
          setUser(prev => prev ? { ...prev, profile_updated_at: res.updatedAt } : null);
        })
        .catch(e => console.error("Failed to save profile to cloud:", e));

    } catch (e) {
      console.error("Failed to save profile:", e);
    }
  };

  const getDistance = (p1: { lat: number, lng: number }, p2: { lat: number, lng: number }) => {
    const R = 6371;
    const dLat = (p2.lat - p1.lat) * Math.PI / 180;
    const dLon = (p2.lng - p1.lng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const routeStats = useMemo(() => {
    if (!route || !Array.isArray(route) || route.length < 2) return { distance: 0, type: '-', isFai: false, triangle: null };
    
    const cleanRoute = route.filter(p => p && typeof p.lat === 'number' && !isNaN(p.lat) && typeof p.lng === 'number' && !isNaN(p.lng));
    const score = evaluateRoute(cleanRoute);
    let type = lang === 'de' ? 'Freie Strecke' : 'Open Distance';
    let isFai = false;
    let triangleData = null;

    if (score.type === 'fai_triangle' || score.type === 'flat_triangle') {
      isFai = score.type === 'fai_triangle';
      if (isFai) {
        type = lang === 'de' ? 'FAI Dreieck' : 'FAI Triangle';
      } else {
        type = lang === 'de' ? 'Flaches Dreieck' : 'Flat Triangle';
      }
      
      if (score.points) {
        triangleData = {
          points: score.points.map((p, idx) => ({
            lat: p.lat,
            lng: p.lng,
            name: `TP${idx + 1}`
          })),
          legs: score.legs || [],
          isFai: isFai // Pass to children
        };
      }
    }

    return { distance: score.distance, type, isFai, triangle: triangleData };
  }, [route, lang]);

  // Reactive Dual View
  const isDualView = (view === 'chat' || view === 'planner') && windowWidth >= 1024;

  const handleJumpTo = (lat: number, lng: number, zoom: number = 14) => {
    setMapTarget({ lat, lng, zoom });
    setView('map');
  };

  // Admin Reset Key to force remount
  const [adminKey, setAdminKey] = useState(0);
  const [profileKey, setProfileKey] = useState(0); // Reset ProfileManager to Menu

  const [isProfileDirty, setIsProfileDirty] = useState(false);
  const [profileSaveTrigger, setProfileSaveTrigger] = useState(0);

  // --- BULLETPROOF PLANNER SYNC ---
  useEffect(() => {
    if (view !== 'planner' && view !== 'map' && view !== 'chat') {
      if (isManualPlanning) setIsManualPlanning(false);
      if (route.length > 0) setRoute([]);
    }
  }, [view, isManualPlanning, route.length]);

  const handleTabChange = (tab: any) => {
    if (tab === 'features') {
      setShowFeatureList(true);
      return;
    }

    const planningSafeViews = ['map', 'planner', 'library', 'chat'];

    // Profile Lock Intercept
    if (isProfileIncomplete && tab !== 'profile' && tab !== 'menu' && tab !== 'market' && tab !== 'map') {
      setShowProfileLockModal(true);
      return;
    }

    // Profile Unsaved Changes Intercept
    if (view === 'profile' && isProfileDirty && tab !== 'profile') {
      setPendingNavigation({ type: 'profile_unsaved', target: tab });
      return;
    }

    // If currently planning and trying to navigate to a non-safe view
    if (isManualPlanning && !planningSafeViews.includes(tab)) {
      if (route.length > 0) {
        setPendingNavigation(tab); // Ask nicely in UI!
        return; // Wait for UI response.
      }
      // Clean exit from planning
      setIsManualPlanning(false);
      setRoute([]);
      setMapPlan(null);
      setAnalysis(null);
      setPlannerConfig(prev => ({ ...prev, routeVariants: [], activeVariantIndex: 0, activeTool: 'pan', mode: 'pan' }));
      setLoadedMissionId(null);
      setLoadedMissionName(null);
    } else if (!isManualPlanning && !planningSafeViews.includes(tab)) {
      // Legacy cleanup if navigating around while NOT planning
      setIsManualPlanning(false);
      setRoute([]);
    }

    if (tab === 'admin' && view === 'admin') {
      setAdminKey(prev => prev + 1);
    }
    if (tab === 'profile' && view === 'profile') {
      setProfileKey(prev => prev + 1);
    }

    if (tab !== 'map' && tab !== 'planner') {
      setFlightAnalysisData(null);
    }

    if (tab === 'planner') {
      setIsManualPlanning(true);
    }

    setView(tab);
  };

  // STABLE HANDLERS for SwisstopoMap (Required for React.memo to work)
  const handleClearFlownTrack = useCallback(() => {
    setFlownTrack(null);
    setComparisonTracks([]);
    setFlightAnalysisData(null);
  }, []);

  const handleAnalyzeFlownTrack = useCallback(() => {
    setChatPrompt("Analysiere meinen hochgeladenen Track. Was war gut, was kann ich besser machen?");
    setView('chat');
  }, []);

  const handleShareFlownTrack = useCallback(() => {
    setView('buddies');
  }, []);
  // Landing Page disabled — users go directly to login

  // Verification Route
  if (window.location.pathname === '/verify') {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      return (
        <ErrorBoundary onReset={() => window.location.reload()}>
          <React.Suspense fallback={<Loading />}>
            <VerificationPage token={token} onVerified={handleLogin} />
          </React.Suspense>
        </ErrorBoundary>
      );
    }
  }

  // Invoice View Route
  if (window.location.pathname.startsWith('/invoice/')) {
    const parts = window.location.pathname.split('/');
    const invId = parts[2]; // /invoice/ID
    if (invId) {
      return (
        <ErrorBoundary onReset={() => window.location.reload()}>
          <React.Suspense fallback={<Loading />}>
            <InvoiceView id={invId} />
          </React.Suspense>
        </ErrorBoundary>
      );
    }
  }

  // Survey Landing Page Route
  if (window.location.pathname.startsWith('/survey/')) {
    const parts = window.location.pathname.split('/');
    const token = parts[parts.length - 1];
    if (token) {
      return (
        <ErrorBoundary onReset={() => window.location.reload()}>
          <React.Suspense fallback={<Loading />}>
            <SurveyLandingPage token={token} lang={lang} />
          </React.Suspense>
        </ErrorBoundary>
      );
    }
  }

  return (
    <ErrorBoundary onReset={handleLogout}>
      <NewsOverlay user={user} />
      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus}
          invoiceId={paymentInvoiceId}
          onClose={() => setPaymentStatus(null)}
        />
      )}
      


      <LegalModal isOpen={showLegalModal} onClose={() => setShowLegalModal(false)} initialTab={legalTab} isAdmin={user?.role === 'superadmin'} />

      {/* Beautiful Profile Lock Modal */}
      {showProfileLockModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-slate-900 border border-sky-500/30 rounded-3xl p-8 max-w-md w-full shadow-[0_20px_60px_rgba(2,132,199,0.2)] flex flex-col items-center text-center relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-32 bg-sky-500/10 blur-[50px] pointer-events-none" />
            
            <div className="relative w-20 h-20 bg-sky-500/10 text-sky-400 rounded-full flex items-center justify-center mb-6 border-4 border-slate-900 shadow-[0_0_0_2px_rgba(56,189,248,0.2)]">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
              <div className="absolute -top-1 -right-1 flex h-6 w-6">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-6 w-6 bg-amber-500 text-amber-950 items-center justify-center font-black text-xs">!</span>
              </div>
            </div>
            
            <h3 className="text-2xl font-black text-white mb-3 uppercase tracking-tight">Profil unvollständig</h3>
            <p className="text-slate-300 text-[15px] leading-relaxed mb-8">
              Um alle Funktionen der Plattform nutzen zu können, musst du zuerst dein <strong className="text-white">Flugprofil</strong> ausfüllen. Wir benötigen ein paar Basics (Schirm, Gurtzeug, Erfahrung), um dir optimale Ergebnisse zu liefern.
            </p>
            
            <button
              onClick={() => {
                setShowProfileLockModal(false);
                setView('profile');
              }}
              className="w-full py-4 bg-gradient-to-r from-sky-600 to-sky-500 hover:from-sky-500 hover:to-sky-400 text-white rounded-2xl font-black text-sm tracking-widest uppercase transition-all shadow-[0_10px_20px_rgba(2,132,199,0.3)] hover:shadow-[0_10px_30px_rgba(2,132,199,0.5)] hover:-translate-y-0.5 active:translate-y-0"
            >
              Jetzt Profil ausfüllen
            </button>
            
            <button
              onClick={() => setShowProfileLockModal(false)}
              className="mt-4 text-xs font-bold text-slate-500 hover:text-slate-300 uppercase tracking-widest transition-colors"
            >
              Später (Eingeschränkte Nutzung)
            </button>
          </div>
        </div>
      )}

      {/* Custom Global Alert Dialog */}
      {globalAlert && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-700/50 rounded-2xl p-6 max-w-sm w-full shadow-[0_10px_40px_rgba(0,0,0,0.6)] flex flex-col gap-4 text-center">
            <div className="mx-auto w-12 h-12 bg-sky-500/10 text-sky-400 rounded-full flex items-center justify-center mb-2 border border-sky-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
            </div>
            <h3 className="text-lg font-black text-white">System Information</h3>
            <p className="text-slate-300 text-sm whitespace-pre-wrap">{globalAlert}</p>
            <button
              onClick={() => setGlobalAlert(null)}
              className="mt-4 w-full px-4 py-3 bg-sky-600 hover:bg-sky-500 hover:shadow-[0_0_15px_rgba(2,132,199,0.4)] text-white rounded-xl text-sm font-bold transition-all shadow-lg focus:outline-none"
            >
              Verstanden
            </button>
          </div>
        </div>
      )}



      {/* Splash Screen for Initial Load (only if configured) */}
      <SplashScreen />

      {/* Landing Page & Auth View */}
      {showLanding ? (
        <LandingPage 
          onEnterApp={(intent = 'login') => {
            setAuthIntent(intent as any);
            setShowLanding(false);
          }} 
          lang={lang} 
          setLang={setLang} 
        />
      ) : !user ? (
        <Auth 
          onLogin={handleLogin} 
          onBack={() => setShowLanding(true)}
          initialView={(authIntent === 'buy' || authIntent === 'register') ? 'register' : 'login'}
          intent={authIntent}
        />
      ) : (
        /* Main App View */
        <div className="h-[100dvh] flex flex-col bg-slate-950 text-slate-200 overflow-hidden select-none">
          <Header
            activeTab={view}
            onTabChange={handleTabChange}
            lang={lang}
            setLang={setLang}
            user={user}
            profile={profile}
            onLogout={handleLogout}
            testModeName={testModeName}
            unreadMsgCount={unreadMsgCount}
            friendRequestCount={friendRequestCount}
            unreadSharedCount={unreadSharedCount}
            onShowLanding={() => setShowLanding(true)}
            profileIncomplete={!!isProfileIncomplete}
            onOpenFeatureList={() => setShowFeatureList(true)}
          />

          {!hasKey && (
            <div className="bg-red-600 text-white px-6 py-2 text-[10px] font-black uppercase tracking-widest z-[3000] flex justify-between items-center shadow-2xl">
              <span>⚠️ API-KEY UNGÜLTIG ODER NICHT BERECHTIGT (400 BAD REQUEST)</span>
              <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/80 transition-colors">Key in Google AI Studio prüfen</a>
            </div>
          )}

          {/* Main Content Area with CSS Variable for Split Width */}
          {/* Full-screen Admin View - rendered OUTSIDE the split pane for full width */}
          {view === 'admin' && (user?.role === 'admin' || user?.role === 'superadmin') && (
            <main className="flex-1 flex flex-col overflow-hidden relative w-full h-full pb-[calc(5.25rem+env(safe-area-inset-bottom,0px))] lg:pb-0">
              <React.Suspense fallback={<Loading />}>
                <AdminDashboard key={adminKey} onExit={() => setView('map')} user={user} />
              </React.Suspense>
            </main>
          )}

          {view !== 'admin' && (() => {
            // Calculation of Visibility State
            const canViewPlanner = !user?.permissions || !!user.permissions.track_planning;

            const showRightPane = isDualView || view !== 'map';

            return (
              <main
                ref={mainRef}
                className="flex-1 flex flex-col lg:flex-row p-0 overflow-hidden relative pb-[calc(5.25rem+env(safe-area-inset-bottom,0px))] lg:pb-0 min-h-0"
              >
                <div
                  className={`relative h-full transition-none split-pane-left ${(isDualView && showRightPane) ? 'dual-view' : 'flex-1'} ${(!isDualView && !showRightPane) ? 'block' : (isDualView ? 'block' : 'hidden')}`}
                  style={(isDualView && showRightPane) ? { width: `${mapWidth}%`, flex: 'none' } : undefined}
                >
                  <React.Suspense fallback={<Loading />}>
                    <SwisstopoMap
                      lang={lang}
                      route={route}
                      flownTrack={flownTrack}
                      comparisonTracks={comparisonTracks}
                      onRouteUpdate={setRoute}
                      triangle={routeStats.triangle}
                      user={user}
                      flyTo={mapTarget}
                      onClearFlyTo={() => setMapTarget(null)}
                      onClearFlownTrack={handleClearFlownTrack}
                      onAnalyzeFlownTrack={handleAnalyzeFlownTrack}
                      onShareFlownTrack={handleShareFlownTrack}
                      isManualPlanning={isManualPlanning}
                      analysisTarget={analysisTarget}
                      onClearAnalysisTarget={() => {
                        setAnalysisTarget(null);
                        if (previousView) {
                          setView(previousView);
                          setPreviousView(null);
                        }
                      }}
                      chatMapAction={chatMapAction}
                      onClearChatMapAction={() => setChatMapAction(null)}
                      chatMapActions={chatMapActions}
                      onClearChatMapActions={() => setChatMapActions(null)}
                      mapPlan={mapPlan}
                      flightAnalysisData={flightAnalysisData}
                      onClearFlightAnalysis={() => {
                        setFlightAnalysisData(null);
                        if (previousView) {
                          setView(previousView);
                          setPreviousView(null);
                        }
                      }}
                      sharedWaypointAgl={waypointAgl}
                      onWaypointAglChange={(index: number, agl: number) => setWaypointAgl(prev => ({ ...prev, [index]: agl }))}
                      onStopPlanning={() => setIsManualPlanning(false)}
                        plannerConfig={plannerConfig}
                        setPlannerConfig={setPlannerConfig}
                      onStartPlanning={() => { setIsManualPlanning(true); setView('planner'); }}
                      plannerCustomWind={plannerCustomWind}
                    />
                  </React.Suspense>

                   {isResizing && <div className="absolute inset-0 z-[2000] cursor-col-resize"></div>}

                </div>

                {/* Force Subscription Selection if no plan active and not verifying (Old Modal) */}
                {(user && !user.subscription?.plan && !window.location.pathname.includes('/verify') && !hasDismissedPricing) && (
                  <React.Suspense fallback={
                    <div className="fixed inset-0 z-[6000] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fadeIn">
                      <Loading />
                    </div>
                  }>
                    <SubscriptionModal
                      user={user}
                      onClose={async () => {
                        localStorage.setItem('xc_pricing_dismissed', 'true');
                        setHasDismissedPricing(true);
                        try {
                          const fresh = await ApiService.getProfile();
                          setUser(fresh);
                          localStorage.setItem('xc_session_user', JSON.stringify(fresh));
                        } catch (e) { }
                      }}
                    />
                  </React.Suspense>
                )}

                {/* Explicitly opened unified Pricing View */}
                {showPricingModal.show && (
                  <React.Suspense fallback={
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-fadeIn overflow-y-auto">
                        <div className="bg-slate-900 border border-white/10 rounded-3xl w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col items-center justify-center min-h-[50vh] relative m-auto">
                            <Loading />
                        </div>
                    </div>
                  }>
                    {React.createElement(PricingView, {
                      user: user,
                      initialTab: showPricingModal.tab,
                      onClose: async () => {
                        setShowPricingModal({ ...showPricingModal, show: false });
                        try {
                          const fresh = await ApiService.getProfile();
                          setUser(fresh);
                          localStorage.setItem('xc_session_user', JSON.stringify(fresh));
                        } catch (e) { }
                      }
                    })}
                  </React.Suspense>
                )}

                {/* FeedbackDialog: always mounted for logged-in users so event listener is always active */}
                <FeedbackDialog user={user} API_BASE_URL={API_BASE_URL} />

                {isDualView && showRightPane && (
                  <div
                    className="hidden lg:flex w-2.5 bg-slate-900 border-x border-white/5 cursor-col-resize hover:bg-sky-600/30 transition-colors items-center justify-center group z-[1500]"
                    onMouseDown={() => setIsResizing(true)}
                  >
                    <div className="w-[1px] h-10 bg-white/20 group-hover:bg-sky-400 rounded-full"></div>
                  </div>
                )}

                {showRightPane && (
                  <div
                    className={`bg-slate-900 border-l border-white/10 transition-all duration-300 split-pane-right ${isDualView ? 'dual-view' : ''} flex flex-col min-h-0 overflow-hidden`}
                    style={(isDualView && showRightPane) ? { width: `${100 - mapWidth}%`, flex: 'none' } : undefined}
                  >
                    <div className={`flex-1 min-h-0 overflow-y-auto custom-scrollbar rounded-2xl ${!isDualView ? 'w-full px-2 sm:px-4 md:px-6' : ''}`}>
                      <React.Suspense fallback={<Loading />}>
                        {view === 'chat' && (
                          (user?.permissions?.ai_consultation) ? (
                            <AdvisorChat
                              user={user}
                              profile={profile}
                              lang={lang}
                              currentRoute={route}
                              flownTrack={flownTrack}
                              onRouteUpdate={setRoute}
                              onError={handleApiError}
                              autoPrompt={chatPrompt}
                              onAutoPromptHandled={() => setChatPrompt(null)}
                              flightTrackId={flightTrackId}
                              initialChatHistory={initialChatHistory}
                              onChatHistoryRestored={() => setInitialChatHistory(null)}
                              onMapAction={(lat, lng, label) => {
                                setChatMapActions(null);
                                setChatMapAction({ lat, lng, label });
                                handleJumpTo(lat, lng, 14);
                              }}
                              onMapActions={(actions) => {
                                setChatMapAction(null);
                                setChatMapActions(actions);
                              }}
                            />
                          ) : (
                            <div className="h-full flex flex-col items-center justify-center p-8 text-center animate-fadeIn">
                              <div className="text-6xl mb-6"><Lock className="w-4 h-4 inline mr-1" /></div>
                              <div className="flex items-center justify-center gap-3 mb-4">
                                <h2 className="text-2xl font-black text-white uppercase tracking-tight">AI Copilot</h2>
                                <ProFeatureBadge featureName="map::KI-Chat" />
                              </div>
                              <p className="text-slate-400 mb-8 max-w-md">
                                Der AI Copilot ist nur im <strong>XC PRO</strong> Abo verfügbar.
                                Analysiere deine Flüge und plane bessere Routen mit künstlicher Intelligenz.
                              </p>
                              <button
                                onClick={() => window.open('https://streckenfliegen.app/pricing', '_blank')}
                                className="bg-sky-600 hover:bg-sky-500 text-white px-8 py-3 rounded-xl font-bold uppercase tracking-widest shadow-lg shadow-sky-900/40 transition-all hover:scale-105"
                              >
                                Upgrade auf PRO
                              </button>
                            </div>
                          )
                        )}
                        {view === 'planner' && <RoutePlanner
                          profile={profile}
                          lang={lang}
                          user={user}
                          onRouteCalculated={setRoute}
                          onAnalysisUpdate={setAnalysis}
                          currentRoute={route}
                          currentAnalysis={analysis}
                          onError={handleApiError}
                          isManualPlanning={isManualPlanning}
                          onToggleManualPlanning={(active: boolean) => {
                            setIsManualPlanning(active);
                            // FIX Problem 3: Auf Mobile zur Karte wechseln wenn Planung startet
                            if (active && windowWidth < 1024) {
                              setView('map');
                            }
                          }}
                          onMapPlanUpdate={setMapPlan}
                          onSelectWaypoint={(index: number) => {
                            const wp = route[index];
                            if (wp) setMapTarget({ lat: wp.lat, lng: wp.lng, zoom: 14 });
                          }}
                          sharedWaypointAgl={waypointAgl}
                          onWaypointAglChange={(index: number, agl: number) => setWaypointAgl(prev => ({ ...prev, [index]: agl }))}
                          onCustomWindChange={setPlannerCustomWind}
                          plannerCustomWind={plannerCustomWind}
                          plannerConfig={plannerConfig}
                          setPlannerConfig={setPlannerConfig}
                          loadedMissionId={loadedMissionId}
                          loadedMissionName={loadedMissionName}
                          onMissionSaved={(id, name) => {
                            setLoadedMissionId(id);
                            if (name) setLoadedMissionName(name);
                          }}
                        />}
                        {view === 'library' && <TrackLibrary lang={lang} user={user} profile={profile} onLoadRoute={(r, variants, mission, noViewChange) => { 
                          setRoute(r); 
                          if (mission) {
                            setLoadedMissionId(mission.id);
                            setLoadedMissionName(mission.name || null);
                          } else {
                            setLoadedMissionId(null);
                            setLoadedMissionName(null);
                          }
                          setIsManualPlanning(true);
                          if (!noViewChange) setView('planner'); 
                          if (r && r.length > 0) {
                              setTimeout(() => window.dispatchEvent(new CustomEvent('fit-route-bounds', { detail: r })), 100);
                          }
                          if (variants && variants.length > 0) {
                            setPlannerConfig(prev => ({ ...prev, routeVariants: variants, activeVariantIndex: 0 }));
                          } else {
                            setPlannerConfig(prev => ({ ...prev, routeVariants: [], activeVariantIndex: 0 }));
                          }
                        }} onLoadFlownTrack={(t, noViewChange) => { setFlownTrack(t); if (!noViewChange) setView('map'); }} onAnalyzeFlownTrack={(t, trackId) => {
                          setFlownTrack(t);
                          setFlightTrackId(trackId || null);
                          setInitialChatHistory(null);
                          setChatPrompt("Analysiere meinen hochgeladenen Track. Was war gut, was kann ich besser machen? Gib mir konkrete Tipps.");
                          setView('chat');
                        }} onViewAnalysis={async (trackId) => {
                          try {
                            const res = await ApiService.apiRequest(`/data/flight-tracks/${trackId}`);
                            if (!res.ok) throw new Error('Laden fehlgeschlagen');
                            const data = await res.json();
                            if (data.chat_history) {
                              const { parseIGC } = await import('./utils/customIgcParser');
                              const igcParsed = parseIGC(data.igc_data);
                              const track = igcParsed.fixes.map((f: any) => ({ lat: f.lat, lng: f.lng, alt: f.pressureAltitude || f.gpsAltitude || 0, time: f.timestamp })).filter((p: any) => p.lat !== 0 && p.lng !== 0);
                              setFlownTrack({ track, date: data.date || igcParsed.date, igcData: igcParsed } as any);
                              setFlightTrackId(trackId);
                              setInitialChatHistory(data.chat_history);
                              setView('chat');
                            }
                          } catch (err) { console.error('Analyse laden fehlgeschlagen:', err); }
                        }} onCompareTracks={(tracks, prompt) => {
                          setComparisonTracks(tracks);
                          if (prompt && typeof prompt === 'string') {
                            setChatPrompt(prompt);
                            setFlownTrack(null);
                            setView('chat');
                          } else {
                            setView('map');
                          }
                        }} onAnalyzeRoute={(r) => {
                          console.log("App: Setting Analysis Target from Library", r);
                          setPreviousView(view);
                          setAnalysisTarget(r);
                          setView('map');
                        }} onFlightAnalysisResult={(data) => {
                          setPreviousView(view);
                          setFlightAnalysisData(data);
                          setView('map');
                        }} />}
                        {view === 'profile' && <ProfileManager key={profileKey} profile={profile} setProfile={saveProfile} lang={lang} user={user} onDirtyChange={setIsProfileDirty} externalSaveTrigger={profileSaveTrigger} onExternalSaveComplete={() => { setIsProfileDirty(false); if (pendingNavigation) { setView(pendingNavigation.target); setPendingNavigation(null); } }} />}


                        {view === 'buddies' && <FlightBuddies user={user} lang={lang} onSendMessage={(u) => { setDraftRecipient(u); setView('messages'); }} />}
                        {view === 'messages' && <Messages user={user} initialRecipient={draftRecipient} onClearDraft={() => setDraftRecipient(null)} />}

                        {view === 'market' && <Marketplace user={user} profile={profile} lang={lang} />}
                        {view === 'statistics' && <PlatformAnalytics user={user} />}
                        {view === 'compare' && <FlightCompare />}

                        {view === 'menu' && <MobileMenu user={user} onNavigate={handleTabChange} lang={lang} onLogout={handleLogout} profileIncomplete={!!isProfileIncomplete} />}
                      </React.Suspense>
                    </div>
                  </div>
                )}
              </main>
            );
          })()}

          {/* Mobile Navigation */}
          <MobileNavBar
            activeTab={view}
            onTabChange={handleTabChange}
            lang={lang}
            user={user}
            unreadMsgCount={unreadMsgCount}
            friendRequestCount={friendRequestCount}
            unreadSharedCount={unreadSharedCount}
            profileIncomplete={!!isProfileIncomplete}
          />

          {showExport && <RouteExport route={route} lang={lang} user={user} onClose={() => setShowExport(false)} />}
          
          {/* Custom Route Exit UI Dialog */}
          {pendingNavigation && (
            <div className="fixed inset-0 z-[99999] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
              <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-[0_10px_40px_rgba(0,0,0,0.8)] flex flex-col gap-4">
                <h3 className="text-xl font-black text-white">
                  {pendingNavigation?.type === 'profile_unsaved' ? 'Ungespeicherte Änderungen' : 'Planung abbrechen?'}
                </h3>
                <p className="text-sm text-slate-400">
                  {pendingNavigation?.type === 'profile_unsaved' 
                    ? 'Du hast ungespeicherte Änderungen in deinem Flugprofil. Möchtest du diese vor dem Verlassen speichern?'
                    : 'Möchtest du die Streckenplanung dieses Mal wirklich verlassen? Deine aktuelle ungespeicherte Strecke geht dabei verloren.'
                  }
                </p>
                
                {pendingNavigation?.type === 'profile_unsaved' ? (
                  <div className="flex flex-col sm:flex-row gap-3 mt-2">
                    <button
                      onClick={() => setPendingNavigation(null)}
                      className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-bold transition-all shadow-lg order-3 sm:order-1"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={() => {
                        setIsProfileDirty(false);
                        setView(pendingNavigation.target);
                        setPendingNavigation(null);
                      }}
                      className="px-4 py-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-xl text-sm font-bold transition-all border border-red-500/30 hover:border-red-500 shadow-lg order-2"
                    >
                      Verwerfen
                    </button>
                    <button
                      onClick={() => {
                        setProfileSaveTrigger(prev => prev + 1);
                      }}
                      className="flex-1 px-4 py-3 bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl text-sm font-bold transition-all shadow-[0_0_20px_rgba(16,185,129,0.3)] order-1 sm:order-3"
                    >
                      Speichern
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-3 mt-2">
                    <button
                      onClick={() => setPendingNavigation(null)}
                      className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-white/5 hover:border-slate-500 text-white rounded-xl text-sm font-bold transition-all shadow-lg"
                    >
                      Bleiben
                    </button>
                    <button
                      onClick={() => {
                        setIsManualPlanning(false);
                        setRoute([]);
                        setMapPlan(null);
                        setAnalysis(null);
                        setPlannerConfig(prev => ({ ...prev, routeVariants: [], activeVariantIndex: 0, activeTool: 'pan', mode: 'pan' }));
                        setLoadedMissionId(null);
                        setLoadedMissionName(null);
                        setView(typeof pendingNavigation === 'string' ? pendingNavigation : pendingNavigation.target);
                        setPendingNavigation(null);
                      }}
                      className="flex-[0.8] px-4 py-3 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-xl text-sm font-bold transition-all border border-red-500/30 hover:border-red-500 shadow-lg"
                    >
                      Verlassen
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GLOBAL DIRECT SNIPPING OVERLAY */}
          {globalSnipActive && (
              <div 
                  className="fixed inset-0 z-[999999] cursor-crosshair select-none"
                  onMouseDown={handleGlobalOverlayMouseDown}
                  onMouseMove={handleGlobalOverlayMouseMove}
                  onMouseUp={handleGlobalOverlayMouseUp}
              >
                  <canvas
                      ref={drawOverlayCanvas}
                      className="absolute inset-0 pointer-events-none"
                  />
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 bg-slate-900/95 text-white border border-white/10 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest shadow-2xl pointer-events-none flex items-center gap-2 animate-bounce">
                      <Scissors className="w-4 h-4 text-emerald-400" />
                      Ziehe einen Rahmen direkt auf dem Bildschirm auf, um den Ausschnitt zu übernehmen
                  </div>
              </div>
          )}

          {/* GLOBAL CREATE NEWS OR SOCIAL BACKGROUND MODAL */}
          {showGlobalNewsModal && globalNewsItem && (
              <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[99999] flex items-center justify-center p-4 animate-in fade-in duration-200">
                  <div className="bg-slate-900 rounded-3xl border border-white/10 p-6 w-full max-w-xl shadow-2xl relative flex flex-col max-h-[95vh] text-slate-200">
                      <button
                          onClick={() => { setShowGlobalNewsModal(false); setGlobalNewsItem(null); }}
                          className="absolute top-5 right-5 sm:top-6 sm:right-6 w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-all shadow-md"
                          title="Schließen"
                      >
                          <X className="w-5 h-5" />
                      </button>
                      
                      <h3 className="text-lg md:text-xl font-bold text-white mb-4 uppercase tracking-tight flex items-center gap-2">
                          <Camera className="w-5 h-5 text-emerald-400" />
                          Screenshot übernehmen
                      </h3>

                      {/* Target Selector: News vs. Social Media */}
                      <div className="flex items-center p-1 bg-slate-950 rounded-2xl border border-white/10 mb-5">
                          <button
                              type="button"
                              onClick={() => setGlobalSnipTarget('news')}
                              className={`flex-1 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 ${
                                  globalSnipTarget === 'news'
                                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/30'
                                      : 'text-slate-400 hover:text-white'
                              }`}
                          >
                              <Newspaper className="w-4 h-4" />
                              <span>📰 News-Meldung</span>
                          </button>
                          <button
                              type="button"
                              onClick={() => setGlobalSnipTarget('social')}
                              className={`flex-1 py-2.5 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 ${
                                  globalSnipTarget === 'social'
                                      ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg shadow-pink-900/30'
                                      : 'text-slate-400 hover:text-white'
                              }`}
                          >
                              <Share2 className="w-4 h-4" />
                              <span>📱 Social Media Mediathek</span>
                          </button>
                      </div>

                      <div className="space-y-5 overflow-y-auto custom-scrollbar flex-1 pr-2">
                          {/* Image Preview */}
                          {globalNewsItem.image_url && (
                              <div className="relative rounded-2xl overflow-hidden border border-white/15 bg-slate-950 flex flex-col">
                                  <img 
                                      src={globalNewsItem.image_url} 
                                      alt="Screenshot Vorschau" 
                                      className="w-full max-h-40 object-contain bg-slate-950/80 p-2"
                                  />
                                  <div className="p-3 bg-slate-950 border-t border-white/5 flex items-center justify-between">
                                      <span className="text-[10px] text-emerald-400 font-black uppercase tracking-wider flex items-center gap-1">
                                          <Check className="w-3.5 h-3.5" /> Bildschirmausschnitt erfasst
                                      </span>
                                      {globalSnipTarget === 'news' && (
                                          <button
                                              type="button"
                                              onClick={handleGlobalNewsAiAnalyze}
                                              disabled={isGlobalAiAnalyzing}
                                              className="flex items-center gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:opacity-50 text-white px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-[0_4px_12px_rgba(99,102,241,0.2)]"
                                          >
                                              {isGlobalAiAnalyzing ? (
                                                  <>
                                                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysiere...
                                                  </>
                                              ) : (
                                                  <>
                                                      <Sparkles className="w-3.5 h-3.5 text-yellow-300" /> KI: Titel & Text
                                                  </>
                                              )}
                                          </button>
                                      )}
                                  </div>
                              </div>
                          )}

                          {/* OPTION 1: NEWS FIELDS */}
                          {globalSnipTarget === 'news' && (
                              <>
                                  {/* Title */}
                                  <div>
                                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Titel</label>
                                      <input
                                          type="text"
                                          value={globalNewsItem.title}
                                          onChange={e => setGlobalNewsItem({ ...globalNewsItem, title: e.target.value })}
                                          className="w-full bg-slate-950 rounded-xl px-4 py-3 text-white border border-white/10 focus:outline-none focus:border-sky-500 text-sm font-bold"
                                          placeholder="Titel der News-Meldung..."
                                      />
                                  </div>

                                  {/* Content */}
                                  <div>
                                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Inhalt / Text</label>
                                      <textarea
                                          value={globalNewsItem.content}
                                          onChange={e => setGlobalNewsItem({ ...globalNewsItem, content: e.target.value })}
                                          className="w-full bg-slate-950 rounded-xl px-4 py-3 text-white border border-white/10 focus:outline-none focus:border-sky-500 text-sm min-h-[100px] custom-scrollbar resize-y font-medium"
                                          placeholder="Beschreibe die Neuigkeit..."
                                      />
                                  </div>

                                  {/* Row for Category & Icon */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 relative">
                                      <div>
                                          <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Kategorie</label>
                                          <select
                                              value={globalNewsItem.category}
                                              onChange={e => setGlobalNewsItem({ ...globalNewsItem, category: e.target.value })}
                                              className="w-full bg-slate-950 rounded-xl px-4 py-3 text-white border border-white/10 focus:outline-none focus:border-sky-500 text-sm font-medium"
                                          >
                                              <option value="Allgemein">Allgemein</option>
                                              <option value="Analyse">Analyse</option>
                                              <option value="Wetter">Wetter</option>
                                              <option value="System">System</option>
                                              <option value="Feature">Feature</option>
                                              <option value="Sicherheit">Sicherheit</option>
                                              <option value="Abo">Abo</option>
                                              <option value="Event">Event</option>
                                              {globalNewsItem.category && !['Allgemein', 'Analyse', 'Wetter', 'System', 'Feature', 'Sicherheit', 'Abo', 'Event'].includes(globalNewsItem.category) && (
                                                  <option value={globalNewsItem.category}>{globalNewsItem.category}</option>
                                              )}
                                          </select>
                                      </div>

                                      <div>
                                          <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Icon</label>
                                          <select
                                              value={globalNewsItem.icon}
                                              onChange={e => setGlobalNewsItem({ ...globalNewsItem, icon: e.target.value })}
                                              className="w-full bg-slate-950 rounded-xl px-4 py-3 text-white border border-white/10 focus:outline-none focus:border-sky-500 text-sm font-medium"
                                          >
                                              <option value='<Newspaper className="w-4 h-4 inline mr-1" />'>Newspaper 📰</option>
                                              <option value='<BarChart2 className="w-4 h-4 inline mr-1" />'>Analyse 📊</option>
                                              <option value='<Wind className="w-4 h-4 inline mr-1" />'>Wind 💨</option>
                                              <option value='<CloudSun className="w-4 h-4 inline mr-1" />'>Meteo ⛅</option>
                                              <option value='<Lock className="w-4 h-4 inline mr-1" />'>Lock 🔒</option>
                                              <option value='<Gem className="w-4 h-4 inline mr-1" />'>Abo 💎</option>
                                          </select>
                                      </div>
                                  </div>
                              </>
                          )}

                          {/* OPTION 2: SOCIAL MEDIA MEDIATHEK FIELDS */}
                          {globalSnipTarget === 'social' && (
                              <div className="space-y-4">
                                  <div className="p-3.5 bg-gradient-to-r from-pink-950/40 to-purple-950/40 border border-pink-500/30 rounded-2xl">
                                      <p className="text-xs text-pink-200 leading-relaxed">
                                          Dieses Bild wird direkt in Azure Blob Storage hochgeladen und steht dir sofort in der <strong>Social Media Mediathek</strong> als Hintergrund für Posts & Stories zur Verfügung.
                                      </p>
                                  </div>

                                  <div>
                                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Bild-Titel / Name</label>
                                      <input
                                          type="text"
                                          value={socialBgTitle || globalNewsItem.title}
                                          onChange={e => setSocialBgTitle(e.target.value)}
                                          className="w-full bg-slate-950 rounded-xl px-4 py-3 text-white border border-pink-500/30 focus:border-pink-400 focus:outline-none text-sm font-bold"
                                          placeholder="z.B. Meteo Radar Screenshot Niesen..."
                                      />
                                  </div>

                                  <div>
                                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">Mediathek-Kategorie</label>
                                      <select
                                          value={socialBgCategory}
                                          onChange={e => setSocialBgCategory(e.target.value)}
                                          className="w-full bg-slate-950 rounded-xl px-4 py-3 text-white border border-slate-700/80 focus:outline-none focus:border-pink-500 text-sm"
                                      >
                                          <option value="Screenshots">📸 Screenshots</option>
                                          <option value="Alpen & Berge">🏔️ Alpen & Berge</option>
                                          <option value="Startplatz & Vorbereitung">🪂 Startplatz & Vorbereitung</option>
                                          <option value="Atmosphäre & Sunset">🌅 Atmosphäre & Sunset</option>
                                          <option value="Thermik & Wolken">☁️ Thermik & Wolken</option>
                                          <option value="Community">👥 Community</option>
                                          <option value="Eigene Uploads">📁 Eigene Uploads</option>
                                      </select>
                                  </div>
                              </div>
                          )}
                      </div>

                      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/5 shrink-0">
                          <button
                              onClick={() => { setShowGlobalNewsModal(false); setGlobalNewsItem(null); }}
                              className="px-5 py-2.5 rounded-xl text-slate-400 hover:text-white font-bold text-xs uppercase tracking-wider"
                              disabled={isGlobalNewsSaving || isGlobalSocialSaving}
                          >
                              Abbrechen
                          </button>

                          {globalSnipTarget === 'news' ? (
                              <button
                                  onClick={handleGlobalNewsSave}
                                  className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-500 text-xs uppercase tracking-wider flex items-center gap-1.5 shadow-lg shadow-emerald-950/20 disabled:opacity-50"
                                  disabled={isGlobalNewsSaving}
                              >
                                  {isGlobalNewsSaving ? (
                                      <>
                                          <Loader2 className="w-4 h-4 animate-spin" /> Speichere News...
                                      </>
                                  ) : (
                                      <>
                                          <Check className="w-4 h-4" /> News veröffentlichen
                                      </>
                                  )}
                              </button>
                          ) : (
                              <button
                                  onClick={handleGlobalSocialSave}
                                  className="px-6 py-2.5 bg-gradient-to-r from-pink-600 via-rose-600 to-purple-600 text-white rounded-xl font-bold hover:opacity-95 text-xs uppercase tracking-wider flex items-center gap-1.5 shadow-lg shadow-pink-950/30 disabled:opacity-50"
                                  disabled={isGlobalSocialSaving}
                              >
                                  {isGlobalSocialSaving ? (
                                      <>
                                          <Loader2 className="w-4 h-4 animate-spin" /> Speichere in Mediathek...
                                      </>
                                  ) : (
                                      <>
                                          <Sparkles className="w-4 h-4" /> In Mediathek speichern
                                      </>
                                  )}
                              </button>
                          )}
                      </div>
                  </div>
              </div>
          )}

          {/* Platform Features Timeline & Changelog Modal */}
          <FeatureListModal
            isOpen={showFeatureList}
            onClose={() => setShowFeatureList(false)}
            user={user}
            onNavigateTab={(targetTab) => handleTabChange(targetTab)}
          />

        </div>
      )}
    </ErrorBoundary>
  );
};

export default App;
